import type { ModuleMigrationDef } from "@kubdee/sdk";
import type { CapabilityName } from "../capabilities";

// Issue #249 M7: typed constant — a typo becomes a compile error.
const CAPABILITIES = JSON.stringify(["calendar"] satisfies CapabilityName[]);

/**
 * Seed the `google-calendar` connector type row (P8 — issue #242).
 *
 * - Idempotent via `INSERT OR IGNORE`: re-running the migration after P8
 *   follow-ups won't overwrite operator edits to the row (e.g. scope
 *   changes, icon swaps).
 * - Primary key `id` equals `slug` so the capability registry and webhook
 *   ingress route both address the row by the same string. This matches the
 *   pattern used by migrations 002 / 003 / 004 (FB / Line / IG).
 * - `oauth_config` carries Google's OAuth 2.1 endpoints. The scheduler DO
 *   (`scheduler.do.ts → dispatchTokenRefresh`) reads
 *   `{ tokenUrl, clientId, clientSecret? }` so the field names match the
 *   other seeds. `primaryMode: "oauth"` distinguishes from Line OA (which
 *   uses `channel_access_token`). `scopes` covers both Calendar reading
 *   (calendar) and event CRUD (calendar.events) — Google recommends
 *   requesting both for clearer audit logs on the consent screen.
 * - `webhook_config` records the push-notification shape the adapter
 *   enforces: a shared-secret header (`x-goog-channel-token`) instead of an
 *   HMAC. This is Google's documented mechanism — there is no HMAC path.
 *   The secret is per-instance (minted in /watch and stored on
 *   `connector_instances.config.channelToken`), so there's no secretEnv
 *   like the FB/Line seeds have.
 * - `icon: 'CalendarDays'` is the canonical Lucide key in the generated
 *   icon registry (`packages/ui/src/lib/generated/icon-registry.ts`).
 *   `color: '#4285F4'` is Google's documented brand blue.
 * - `created_at` / `updated_at` use a fixed seed timestamp — tenants-local
 *   time isn't available at seed time, and the column is audit-only.
 */
export const migration: ModuleMigrationDef = {
  key: "005_google_calendar_seed",
  version: "0.5.0",
  sql: `
INSERT OR IGNORE INTO \`connectors_types\` (
  id, slug, name, icon, icon_url, capabilities, oauth_config, webhook_config,
  is_active, created_at, updated_at
) VALUES (
  'google-calendar',
  'google-calendar',
  'Google Calendar',
  'CalendarDays',
  NULL,
  '${CAPABILITIES}',
  '{"primaryMode":"oauth","tokenUrl":"https://oauth2.googleapis.com/token","authorizeUrl":"https://accounts.google.com/o/oauth2/v2/auth","tokeninfoUrl":"https://www.googleapis.com/oauth2/v3/tokeninfo","clientIdEnv":"GOOGLE_CALENDAR_CLIENT_ID","clientSecretEnv":"GOOGLE_CALENDAR_CLIENT_SECRET","scopes":["https://www.googleapis.com/auth/calendar","https://www.googleapis.com/auth/calendar.events"],"extraAuthorizeParams":{"access_type":"offline","prompt":"consent","include_granted_scopes":"true"},"color":"#4285F4","notes":"Refresh tokens are returned only when prompt=consent is used. Google revokes refresh tokens after 6 months of inactivity; callers should surface 400 invalid_grant as a re-auth requirement."}',
  '{"signatureHeader":"x-goog-channel-token","algorithm":"shared-secret","encoding":"none","secretSource":"per_instance_config.channelToken","resourceStateHeader":"x-goog-resource-state","messageNumberHeader":"x-goog-message-number","notes":"Google Calendar push notifications carry no body. The webhook receiver only learns that the calendar changed; a follow-up listEvents({ syncToken }) call resolves the delta. Watch channels have a max TTL of 30 days and must be refreshed via a scheduler job."}',
  1,
  '2026-04-17T00:00:00Z',
  '2026-04-17T00:00:00Z'
);
  `.trim(),
};
